import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { formatDistanceToNow } from 'date-fns';
import {
  ArrowPathIcon,
  ArrowPathRoundedSquareIcon,
  WrenchScrewdriverIcon,
  ChatBubbleLeftIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import api from '../../services/api';

interface DeviceCommandsTabProps {
  deviceId: string;
}

const STATUS_STYLES: Record<string, string> = {
  pending: 'bg-yellow-900/30 text-yellow-300 border border-yellow-700/50',
  delivered: 'bg-blue-900/30 text-blue-300 border border-blue-700/50',
  completed: 'bg-green-900/30 text-green-300 border border-green-700/50',
  failed: 'bg-red-900/30 text-red-300 border border-red-700/50',
  cancelled: 'bg-slate-700/50 text-slate-400 border border-slate-600/50',
};

function formatCommandName(command: string): string {
  return command
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

function DeviceCommandsTab({ deviceId }: DeviceCommandsTabProps) {
  const queryClient = useQueryClient();
  const [showMessageDialog, setShowMessageDialog] = useState(false);
  const [messageText, setMessageText] = useState('');
  const [messageLevel, setMessageLevel] = useState<'info' | 'warning' | 'critical'>('info');

  // Fetch command history
  const { data } = useQuery({
    queryKey: ['device-commands', deviceId],
    queryFn: () => api.adminGetDeviceCommands(deviceId),
    refetchInterval: 10000,
  });

  const commands = data?.commands || [];

  // Mutation for sending commands
  const sendCommandMutation = useMutation({
    mutationFn: ({
      command,
      params,
    }: {
      command: string;
      params?: Record<string, unknown>;
    }) => api.adminCreateDeviceCommand(deviceId, command, params),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['device-commands', deviceId] });
    },
  });

  // Mutation for cancelling commands
  const cancelCommandMutation = useMutation({
    mutationFn: (commandId: string) =>
      api.adminCancelDeviceCommand(deviceId, commandId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['device-commands', deviceId] });
    },
  });

  const handleSendCommand = (command: string, params?: Record<string, unknown>) => {
    sendCommandMutation.mutate({ command, params });
  };

  const handleSendMessage = () => {
    if (!messageText.trim()) return;
    sendCommandMutation.mutate(
      {
        command: 'message',
        params: { text: messageText.trim(), level: messageLevel },
      },
      {
        onSuccess: () => {
          setShowMessageDialog(false);
          setMessageText('');
          setMessageLevel('info');
        },
      }
    );
  };

  const handleCancelCommand = (commandId: string) => {
    cancelCommandMutation.mutate(commandId);
  };

  return (
    <div className="space-y-6">
      {/* Command Buttons */}
      <div className="rounded-lg border border-slate-700 bg-slate-800/50 p-5">
        <h3 className="text-sm font-semibold text-slate-200 uppercase tracking-wider mb-4">
          Send Command
        </h3>
        <div className="flex flex-wrap gap-3">
          <button
            onClick={() => handleSendCommand('sync_now')}
            disabled={sendCommandMutation.isPending}
            className="flex items-center gap-2 bg-slate-700 hover:bg-slate-600 disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-lg px-4 py-3 transition-colors"
          >
            <ArrowPathIcon className="h-5 w-5" />
            Sync Now
          </button>

          <button
            onClick={() => handleSendCommand('restart_la')}
            disabled={sendCommandMutation.isPending}
            className="flex items-center gap-2 bg-slate-700 hover:bg-slate-600 disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-lg px-4 py-3 transition-colors"
          >
            <ArrowPathRoundedSquareIcon className="h-5 w-5" />
            Restart LA
          </button>

          <button
            onClick={() => handleSendCommand('collect_diagnostics')}
            disabled={sendCommandMutation.isPending}
            className="flex items-center gap-2 bg-slate-700 hover:bg-slate-600 disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-lg px-4 py-3 transition-colors"
          >
            <WrenchScrewdriverIcon className="h-5 w-5" />
            Collect Diagnostics
          </button>

          <button
            onClick={() => setShowMessageDialog(true)}
            disabled={sendCommandMutation.isPending}
            className="flex items-center gap-2 bg-slate-700 hover:bg-slate-600 disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-lg px-4 py-3 transition-colors"
          >
            <ChatBubbleLeftIcon className="h-5 w-5" />
            Send Message
          </button>
        </div>

        {sendCommandMutation.isError && (
          <p className="mt-3 text-sm text-red-400">
            Failed to send command. Please try again.
          </p>
        )}
        {sendCommandMutation.isSuccess && (
          <p className="mt-3 text-sm text-green-400">
            Command sent successfully.
          </p>
        )}
      </div>

      {/* Message Dialog */}
      {showMessageDialog && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60">
          <div className="bg-slate-800 border border-slate-700 rounded-xl shadow-2xl w-full max-w-md p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-white">Send Message to Device</h3>
              <button
                onClick={() => {
                  setShowMessageDialog(false);
                  setMessageText('');
                  setMessageLevel('info');
                }}
                className="text-slate-400 hover:text-white transition-colors"
              >
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-1">
                  Message
                </label>
                <textarea
                  value={messageText}
                  onChange={(e) => setMessageText(e.target.value)}
                  rows={3}
                  className="w-full rounded-lg border border-slate-600 bg-slate-700/50 text-white px-3 py-2 text-sm placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Enter message to send to device..."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-300 mb-1">
                  Level
                </label>
                <div className="flex gap-2">
                  {(['info', 'warning', 'critical'] as const).map((level) => (
                    <button
                      key={level}
                      onClick={() => setMessageLevel(level)}
                      className={`px-3 py-1.5 rounded-md text-sm font-medium capitalize transition-colors ${
                        messageLevel === level
                          ? level === 'info'
                            ? 'bg-blue-600 text-white'
                            : level === 'warning'
                              ? 'bg-yellow-600 text-white'
                              : 'bg-red-600 text-white'
                          : 'bg-slate-700 text-slate-300 hover:bg-slate-600'
                      }`}
                    >
                      {level}
                    </button>
                  ))}
                </div>
              </div>

              <div className="flex justify-end gap-3 pt-2">
                <button
                  onClick={() => {
                    setShowMessageDialog(false);
                    setMessageText('');
                    setMessageLevel('info');
                  }}
                  className="px-4 py-2 text-sm text-slate-300 hover:text-white transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSendMessage}
                  disabled={!messageText.trim() || sendCommandMutation.isPending}
                  className="px-4 py-2 bg-blue-600 hover:bg-blue-500 disabled:opacity-50 disabled:cursor-not-allowed text-white text-sm font-medium rounded-lg transition-colors"
                >
                  {sendCommandMutation.isPending ? 'Sending...' : 'Send'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Command History */}
      <div className="rounded-lg border border-slate-700 bg-slate-800/50 p-5">
        <h3 className="text-sm font-semibold text-slate-200 uppercase tracking-wider mb-4">
          Command History
        </h3>

        {commands.length === 0 ? (
          <p className="text-sm text-slate-500">No commands have been sent to this device.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-700 text-left">
                  <th className="pb-3 pr-4 text-xs font-semibold text-slate-400 uppercase tracking-wider">
                    Command
                  </th>
                  <th className="pb-3 pr-4 text-xs font-semibold text-slate-400 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="pb-3 pr-4 text-xs font-semibold text-slate-400 uppercase tracking-wider">
                    Created
                  </th>
                  <th className="pb-3 pr-4 text-xs font-semibold text-slate-400 uppercase tracking-wider">
                    Completed
                  </th>
                  <th className="pb-3 pr-4 text-xs font-semibold text-slate-400 uppercase tracking-wider">
                    Created By
                  </th>
                  <th className="pb-3 text-xs font-semibold text-slate-400 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-700/50">
                {commands.map((cmd: any) => (
                  <tr key={cmd.id} className="text-slate-300">
                    <td className="py-3 pr-4 font-medium text-white">
                      {formatCommandName(cmd.command)}
                    </td>
                    <td className="py-3 pr-4">
                      <span
                        className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${
                          STATUS_STYLES[cmd.status] || STATUS_STYLES.pending
                        }`}
                      >
                        {cmd.status}
                      </span>
                    </td>
                    <td className="py-3 pr-4 text-slate-400">
                      {cmd.created_at
                        ? formatDistanceToNow(new Date(cmd.created_at), { addSuffix: true })
                        : '\u2014'}
                    </td>
                    <td className="py-3 pr-4 text-slate-400">
                      {cmd.completed_at
                        ? formatDistanceToNow(new Date(cmd.completed_at), { addSuffix: true })
                        : '\u2014'}
                    </td>
                    <td className="py-3 pr-4 text-slate-400">
                      {cmd.created_by_email || cmd.created_by || '\u2014'}
                    </td>
                    <td className="py-3">
                      {(cmd.status === 'pending' || cmd.status === 'delivered') && (
                        <button
                          onClick={() => handleCancelCommand(cmd.id)}
                          disabled={cancelCommandMutation.isPending}
                          className="text-xs text-red-400 hover:text-red-300 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                        >
                          Cancel
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

export default DeviceCommandsTab;
